import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Row, Col, ListGroup, Image, Button, Card, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import Message from "../components/Message";
import Loader from "../components/Loader";
import {
  useGetOrderDetailsQuery,
  usePayOrderMutation,
  useCreateRazorPayOrderMutation,
  useUpdatePreferredDeliveryMutation
} from "../slices/ordersApiSlice";
import { useGetAddressByIdQuery } from "../slices/addressApiSlice";
import { useSendOrderUpdatedEmailMutation } from "../slices/emailApiSlice";
import AssignSourcingPartner from "../components/AssignSourcingPartner.jsx";
import AssignDeliveryPartner from "../components/AssignDeliveryPartner.jsx";
import OrderReview from "../components/OrderReview.jsx";
import OrderProductReview from "../components/OrderProductReview.jsx";
import { RAZORPAY_URL } from "../constants";
import razorpay_logo from '../assets/razorpay_logo.png';
import flavchap_tpv_logo from '../assets/flavchap_tpv_logo.png';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';
import {
  toIST,
  getAvailableDeliverySlots,
  getDayLabel,
  dateFormatter,
  istDateKey,
  addDaysIST,
  buildSlotStartIST
} from "../utils/dateHelper.js";

const OrderScreen = () => {
  const {id: orderId} = useParams();
  
  const {data: order, refetch, isLoading, error} = useGetOrderDetailsQuery(orderId);

  const {data: address, isLoading: isLoadingGetAddressById, error: isErrorGetAddressById} = useGetAddressByIdQuery(order?.shippingAddress, { skip: !order?.shippingAddress });

  const [createRazorPayOrder] = useCreateRazorPayOrderMutation();

  const [payOrder, {isLoading: isLoadingPay}] = usePayOrderMutation();

  const [updatePreferredDelivery] = useUpdatePreferredDeliveryMutation();

  const { userInfo } = useSelector((state) => state.auth);

  const [selectedDeliveryDateOffset, setSelectedDeliveryDateOffset] = useState(null);

  const [selectedDeliveryTimeSlot, setSelectedDeliveryTimeSlot] = useState(null);
  const [finalizedDeliveryTimeSlot, setFinalizedDeliveryTimeSlot] = useState(null);
  const [savingDeliveryTimeSlot, setSavingDeliveryTimeSlot] = useState(false);

  const [sendOrderUpdatedEmail] = useSendOrderUpdatedEmailMutation();

  // Compute the default slot's absolute IST start time (if set)
  let orderDefaultSlotStartIST = null;
  if (order?.delivery?.preferredDeliveryDate && order?.delivery?.preferredDeliveryTimeSlot) {
    // order.delivery.preferredDeliveryDate is stored as midnight IST (as UTC instant)
    const defaultDayIST = toIST(new Date(order.delivery.preferredDeliveryDate)); // returns IST Date
    orderDefaultSlotStartIST = buildSlotStartIST(defaultDayIST, order.delivery.preferredDeliveryTimeSlot);
  }
  
  useEffect(() => {
    if (order?.delivery?.preferredDeliveryTimeSlot) {
      setFinalizedDeliveryTimeSlot(order.delivery.preferredDeliveryTimeSlot);
    }
  }, [order]);

  const handleSlotSelect = (slot, dayOffset) => {
    setSelectedDeliveryDateOffset(dayOffset);
    setSelectedDeliveryTimeSlot(slot);
  }

  const savePreferredSlot = async () => {
    try {
      setSavingDeliveryTimeSlot(true);
      const preferredDeliveryDateOffset = selectedDeliveryDateOffset;
      const preferredDeliveryTimeSlot = selectedDeliveryTimeSlot;
      const updatedOrder = await updatePreferredDelivery({
        orderId,
        preferredDeliveryDateOffset,
        preferredDeliveryTimeSlot
      }).unwrap();
      await refetch();
      setFinalizedDeliveryTimeSlot(updatedOrder.delivery.preferredDeliveryTimeSlot);
      toast.success("Preferred delivery slot updated", {autoClose: 1000});
      await sendOrderUpdatedEmail(updatedOrder);
    } catch (err) {
      toast.error("Failed to update preferred slot", {autoClose: 2000});
    } finally {
      setSavingDeliveryTimeSlot(false);
    }
  };

  function loadScript(src) {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    })
  };

  async function displayRazorPay() {
    const res = await loadScript(RAZORPAY_URL);
    if (!res) {
      alert("RazorPay failed to load. Please check your connection");
      return;
    }

    const createRazorPayOrderResult = await createRazorPayOrder(orderId);
    if (!createRazorPayOrderResult) {
      alert("RazorPay could not process order. Please check your connection");
    }

    const {amount, id: razorPayOrderId, currency} = createRazorPayOrderResult.data;

    const razorPayKeyId = process.env.REACT_APP_RAZORPAY_KEY_ID;
    const businessName = process.env.REACT_APP_BUSINESS_NAME;
    const razorPayThemeColor = process.env.REACT_APP_RAZORPAY_THEME_COLOR;

    const options = {
      key: razorPayKeyId,
      amount: amount,
      currency: currency,
      name: businessName,
      description: `Payment for order ${razorPayOrderId}`,
      image: flavchap_tpv_logo,
      order_id: razorPayOrderId,
      handler: async function (response) {
        const details = {
            orderCreationId: razorPayOrderId,
            razorPayPaymentId: response.razorpay_payment_id,
            razorPayResponseOrderId: response.razorpay_order_id,
            razorPaySignature: response.razorpay_signature,
        };

        try {
          await payOrder({orderId, details});
          refetch();
          toast.success('Payment successful', {autoClose: 1000});
        } catch (error) {
          toast.error(error?.data?.message || error.message);
        }

      },
      prefill: {
        name: userInfo.name,
        email: userInfo.email,
        contact: userInfo.phone
      },
      notes: {
        address: "Some Address"
      },
      theme: {
        color: razorPayThemeColor
      }
    };
    const paymentObject = new window.Razorpay(options);
    paymentObject.open();
  }
  
  // 1) Slots & grouping
  const slots = order ? getAvailableDeliverySlots(order.createdAt) : [];

  const groupedSlots = slots.reduce((acc, s) => {
    if (!acc[s.dayOffset]) acc[s.dayOffset] = [];
    acc[s.dayOffset].push(s.slot);
    return acc;
  }, {});

  // 2) Precompute IST/day keys
  const createdIST = order ? toIST(new Date(order.createdAt)) : null;

  const finalizedPreferredDateKey = order?.delivery?.preferredDeliveryDate
    ? istDateKey(order.delivery.preferredDeliveryDate)
    : null;

  const selectedPreferredDateKey =
    createdIST != null && selectedDeliveryDateOffset != null
      ? istDateKey(addDaysIST(createdIST, Number(selectedDeliveryDateOffset)))
      : null;

  // 3) Save button visibility
  const showSaveButton =
    selectedDeliveryTimeSlot &&
    (
      !finalizedPreferredDateKey ||
      selectedPreferredDateKey !== finalizedPreferredDateKey ||
      selectedDeliveryTimeSlot !== finalizedDeliveryTimeSlot
    );

  return isLoading ? <Loader/> : error ? <Message variant="danger">{error?.data?.message||error.error}</Message> : (
    <>
      <h1>Order Details</h1>
      <p><strong>Order ID:</strong> {order?._id || "Try reloading the page"}</p>
      <Row>
        <Col md={8}>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>Shipping</h2>
              {
              isErrorGetAddressById ? <Message variant="danger">{error?.data?.message||error.error}</Message> :
              isLoadingGetAddressById ? <Loader/> : (
              <>
                <p>
                  <strong>Name: </strong>
                  {address?.name}
                </p>
                <p>
                  <strong>Phone: </strong>
                  {order?.user?.phone || "Try reloading the page"}
                </p>
                {order?.user?.email ? order.user.email !== "noemail@noemail.noemail" && (
                  <p>
                    <strong>Email: </strong>
                    {order.user.email}
                  </p>
                ) : (
                  <p><strong>Email: </strong>Try reloading the page</p>
                )}
                <p>
                  <strong>Address: </strong>
                  {address?.addressLine1}, {address?.addressLine2 ? address?.addressLine2 + ',' : ''} {address?.city}, {address?.state}, {address?.postalCode}, {address?.phone}
                </p>
              </>
              )}
              {
                order.delivery.isDelivered ? (
                  <Message variant="success">
                    Delivered on {dateFormatter(order.delivery.deliveredAt)}
                  </Message>
                ) : order.sourcing.isCollected ? (
                  <Message variant="info">
                    Collected on {dateFormatter(order.sourcing.collectedAt)}. To be delivered
                  </Message>
                ) : (
                  <Message variant="danger">
                    Not delivered
                  </Message>
                )
              }
            </ListGroup.Item>
            <ListGroup.Item>
              <h2>Delivery Time Slot</h2>
              {finalizedDeliveryTimeSlot ? (
                <Message variant="success">
                  Current Delivery Time Slot: <strong>
                    {finalizedDeliveryTimeSlot}
                    {order?.delivery?.preferredDeliveryDate && (
                      <>
                      {" on "}
                      {new Date(order.delivery.preferredDeliveryDate).toLocaleDateString("en-IN", {
                        weekday: "long",
                        timeZone: "Asia/Kolkata"
                      })}
                      {", "}
                      {new Date(order.delivery.preferredDeliveryDate).toLocaleDateString("en-IN", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        timeZone: "Asia/Kolkata"
                      })}
                      </>
                    )}
                  </strong>
                </Message>
              ) : (
                <Message variant="warning">No preferred delivery time slot</Message>
              )}

              {/* Slot Selection UI */}
              {slots.length > 0 ? (
                <div className="mt-3">
                  <h5><strong>A preferred delivery time slot can be selected below within 30 minutes of order creation</strong></h5>
                  {Object.entries(groupedSlots).map(([dayOffset, daySlots]) => {
                    const dayDateIST = createdIST ? addDaysIST(createdIST, Number(dayOffset)) : null;
                    const dayKey = dayDateIST ? istDateKey(dayDateIST) : null;

                    return (
                      <div key={dayOffset} className="mb-3">
                        <h6><strong>{getDayLabel(Number(dayOffset))}</strong></h6>
                        <div className="d-flex flex-wrap gap-2">
                        {daySlots.map((slot) => {
                          const isFinalized =
                            finalizedPreferredDateKey &&
                            dayKey &&
                            finalizedPreferredDateKey === dayKey &&
                            finalizedDeliveryTimeSlot === slot;

                          const isSelected =
                            selectedPreferredDateKey &&
                            dayKey &&
                            selectedPreferredDateKey === dayKey &&
                            selectedDeliveryTimeSlot === slot;

                          // Compute absolute IST start for this slot
                          const slotStartIST = createdIST ? buildSlotStartIST(addDaysIST(createdIST, Number(dayOffset)), slot) : null;

                          // Determine whether this candidate slot is strictly earlier than the order's default slot
                          const isEarlierThanDefault = orderDefaultSlotStartIST && slotStartIST && (slotStartIST.getTime() < orderDefaultSlotStartIST.getTime());

                          // Decide button props
                          const variant = isFinalized ? "success" : isSelected ? "primary" : "outline-primary";
                          const disabled = isEarlierThanDefault;

                          // Optional: if you want a different visual for disabled, override variant
                          const effectiveVariant = disabled ? "secondary" : variant;

                          return (
                            <OverlayTrigger
                              key={`${dayOffset}-${slot}`}
                              placement="top"
                              overlay={
                                disabled ? (
                                  <Tooltip id={`tooltip-${dayOffset}-${slot}`}>
                                    This slot is before the order's assigned default slot and cannot be selected.
                                  </Tooltip>
                                ) : <></>
                              }
                            >
                              <span className="d-inline-block" key={`${dayOffset}-${slot}`} style={disabled ? { pointerEvents: 'none' } : {}}>
                                <Button
                                  variant={effectiveVariant}
                                  onClick={() => !disabled && handleSlotSelect(slot, Number(dayOffset))}
                                  disabled={disabled}
                                >
                                  {slot}
                                </Button>
                              </span>
                            </OverlayTrigger>
                          );
                        })}
                        </div>
                      </div>
                    );
                  })}

                  {showSaveButton && (
                    <div className="mt-3">
                      <Button
                        onClick={savePreferredSlot}
                        disabled={savingDeliveryTimeSlot}>
                        {savingDeliveryTimeSlot ? (
                          <>
                            <Spinner animation="border" size="sm" /> Saving...
                          </>
                        ) : (
                          'Save Preferred Delivery Time Slot'
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <Message variant="info">
                  Delivery slot selection period has expired (1 hour past order creation).
                </Message>
              )}
            </ListGroup.Item>
            <ListGroup.Item>
              <h2>Payment</h2>
              <p>
                <strong>Method: </strong>
                {order.paymentMethod}
              </p>
              {
                order.isPaid ? (
                  <Message variant="success">
                    Paid on {dateFormatter(order.paidAt)}
                  </Message>
                ) : (
                  <Message variant="danger">
                    Not paid
                  </Message>
                )
              }
            </ListGroup.Item>
            <ListGroup.Item>
              <h2>Order Items</h2>
              <Message variant="info">
                <strong>
                  Keep the items stored in a cool, dry place. Consume within 48 hours for better freshness.
                </strong>
              </Message>
              {order.orderItems.map((item, index) => (
                <ListGroup.Item key={index}>
                  <Row>
                    <Col xs={6} sm={6} md={2}>
                      <Image src={item.image} alt={item.name} fluid rounded/>
                    </Col>
                    <Col xs={6} sm={6} md={8}>
                      <Row>
                        <span>
                          <Link to={`/product/${item.product}`}>
                            {item.name}
                          </Link>
                          <br/>
                          {(item.spiceLevel !== "N/A" && "Spice Level: " + item.spiceLevel)  || (item.sweetness !== "N/A" && "Sweetness: " +  item.sweetness)}
                        </span>
                      </Row>
                      <Row>
                        <span>
                          Weight: {item.weight}
                        </span>
                      </Row>
                      <Row>
                        <span>
                          Price: {item.quantity} x ₹{item.price} = ₹{item.quantity*item.price}
                        </span>
                      </Row>
                    </Col>
                  </Row>
                </ListGroup.Item>
              ))}
            </ListGroup.Item>
            <OrderReview
              order={order}
              orderId={orderId}
              refetch={refetch}
            />

            <OrderProductReview
              order={order}
              orderId={orderId}
              refetch={refetch}
            />
          </ListGroup>
        </Col>
        <Col md={4}>
          <Card className="h-auto">
            <ListGroup variant="flush">
              <ListGroup.Item>
                <h2>Order Summary</h2>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col>Items</Col>
                  <Col>₹{order.itemsPrice}</Col>
                </Row>
                <Row>
                  <Col>Shipping</Col>
                  <Col>₹{order.shippingPrice}</Col>
                </Row>
                <Row>
                  <Col className="d-flex align-items-center gap-1">
                    <span>Tax And Fees:</span>
                    <OverlayTrigger
                      trigger="click"
                      placement="right"
                      rootClose
                      overlay={
                        <Tooltip id="tax-info-tooltip">
                          GST on Items (5%) = ₹{order.taxPrice}
                          <br />
                          GST on shipping (5%) = ₹{order.shippingTax}
                          <br/>
                          Platform fee = ₹{order.platformFee}
                          <br/>
                          GST on Platform fee (18%) = ₹{order.platformFeeTax}
                        </Tooltip>
                      }
                    >
                      <span style={{ cursor: 'pointer' }}>
                        <FaInfoCircle style={{ color: '#6c757d' }} />
                      </span>
                    </OverlayTrigger>
                  </Col>
                  <Col>₹{order.taxAndFees}</Col>
                </Row>
                <Row>
                  <Col>
                    <strong>Total</strong>
                  </Col>
                  <Col>
                    <strong>₹{order.totalPrice}</strong>
                  </Col>
                </Row>
              </ListGroup.Item>
              <ListGroup.Item>
                <Message variant="info"><strong>Order once placed cannot be cancelled</strong></Message>
              </ListGroup.Item>
              { !order.isPaid && (
                <ListGroup.Item>
                  { isLoadingPay && <Loader/>}
                  <Button
                    type="button"
                    variant="primary"
                    className="btn-block d-flex align-items-center justify-content-center gap-2"
                    disabled={order.orderItems.length === 0}
                    onClick={displayRazorPay}
                    style={{
                      backgroundColor: "#2c66c2",
                      color: "#fff",
                      borderColor: "#2c66c2"
                    }}>
                      <strong>Pay ₹{order.totalPrice} </strong>
                      <img
                        src={razorpay_logo}
                        alt="Razorpay"
                        style={{
                          height: '20px',
                          verticalAlign: 'middle'
                        }}
                        /><strong> & Place Order</strong>
                  </Button>
                </ListGroup.Item>
              )}
            </ListGroup>
          </Card>
          {userInfo?.isAdmin && (
            <AssignSourcingPartner
              order={order}
              orderId={orderId}
              refetch={refetch}
              userInfo={userInfo}
            />
          )}
          {userInfo?.isAdmin && (
            <AssignDeliveryPartner
              order={order}
              orderId={orderId}
              refetch={refetch}
              userInfo={userInfo}
            />
          )}
        </Col>
      </Row>
    </>
  );
};

export default OrderScreen;